'use client'

import React from 'react'
import { Link } from '@/lib'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ArrowSquareOut } from 'phosphor-react'

interface ProjectCardProps {
  title: string
  description: string
  duration: string
  role: string
  tags: string[]
  link: string
  featured?: boolean
  image?: string
}

export default function ProjectCard({ title, description, duration, role, tags, link, featured = false, image }: ProjectCardProps) {
  return (
    <Link to={link} className="block h-full">
      <Card variant="media" className="group relative overflow-hidden brutal-panel hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[6px_6px_0_#141414] transition-all duration-150 cursor-pointer h-full !p-0 !gap-0">
        {image && (
          <div className="relative overflow-hidden border-b-2 border-[var(--brutal-ink)]">
            <img 
              src={image} 
              alt={title}
              className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
            />
          </div>
        )}
        <div className="p-4 flex flex-col gap-2.5">
          {featured && (
            <Badge className="w-fit bg-[var(--brutal-mustard)] text-[var(--brutal-ink)] border-2 border-[var(--brutal-ink)] rounded-full shadow-[var(--brutal-shadow-sm)] hover:bg-[var(--brutal-mustard)]">
              Featured
            </Badge>
          )}
          <h3 className="text-lg font-bold text-[var(--brutal-ink)] group-hover:text-[var(--brutal-accent)] transition-colors duration-150 leading-snug text-balance">
            {title}
          </h3>

          <p className="text-[var(--brutal-muted)] text-sm leading-snug text-pretty">
            {description}
          </p>

          <div className="flex items-baseline justify-between gap-3 text-sm text-[var(--brutal-muted)] font-brutal-mono">
            <span className="shrink-0 whitespace-nowrap font-medium">{duration}</span>
            <span className="min-w-0 text-right font-medium leading-snug">{role}</span>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {tags.map((tag, index) => (
              <Badge 
                key={index} 
                variant="secondary" 
                className="bg-[var(--brutal-kraft)] text-[var(--brutal-ink)] border border-[var(--brutal-ink)] rounded-full text-xs h-fit"
              >
                {tag}
              </Badge>
            ))}
          </div>

          <div className="flex items-center justify-center text-[var(--brutal-ink)] group-hover:text-[var(--brutal-accent)] transition-colors duration-150 pt-1 font-semibold">
            <span className="text-sm">View Project</span>
            <ArrowSquareOut weight="duotone" className="ml-2 h-4 w-4" />
          </div>
        </div>
      </Card>
    </Link>
  )
}
